<template>
    <div class="card" style="min-height: calc(100vh - 180px)">
        <div class="bg-blue-light p-1">
            <TitleButton :showBtn="true" :showAddNew="false" :title="'Order Details'" @onClickCloseButton="navigateToListPage" />
        </div>

        <div class="px-2 mt-1">
            <ul class="nav nav-tabs">
                <li v-for="tab in tabs" :key="tab.id" class="nav-item">
                    <a
                        :class="['nav-link', 'cursor-pointer', { active: activeTab.id === tab.id }]"
                        href="javascript:void(0)"
                        @click="setActiveTab(tab)"
                    >
                        {{ tab.title }}<span v-if="tabCounts[tab.id] !== null"> ({{ tabCounts[tab.id] }})</span>
                    </a>
                </li>
            </ul>
        </div>

        <div v-if="errorState" class="col-12 px-2 mt-2">
            <div class="alert alert-danger p-1 mb-0" role="alert">
                {{ errorState }}
            </div>
        </div>

        <div v-if="!errorState" class="col-12 px-2 mt-2 mb-2">
            <component
                :is="tabComponents[activeTab.id]"
                :orderDetail="orderDetail"
                :onCreateNew="onClickToRedirect"
                :onEdit="onClickToEditCosting"
                :onEditProduction="onClickToEditProduction"
                :onEditChallan="onClickToEditChallan"
                :onPlanChanged="getDetail"
            />
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import cloneDeep from 'lodash/cloneDeep'
import handlePurchase from '@/services/modules/purchase'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import OrderSummaryTab from '@/components/molecule/company/inventory/production-order-tracker/tabs/OrderSummaryTab.vue'
import CostingTab from '@/components/molecule/company/inventory/production-order-tracker/tabs/CostingTab.vue'
import ProductionTab from '@/components/molecule/company/inventory/production-order-tracker/tabs/ProductionTab.vue'
import DeliveryChallanTab from '@/components/molecule/company/inventory/production-order-tracker/tabs/DeliveryChallanTab.vue'
import InvoiceTab from '@/components/molecule/company/inventory/production-order-tracker/tabs/InvoiceTab.vue'
import MoneyReceiptTab from '@/components/molecule/company/inventory/production-order-tracker/tabs/MoneyReceiptTab.vue'

const route = useRoute()
const router = useRouter()
const { fetchOrderTrackerDetail } = handlePurchase()

const loading = ref(false)
const errorState = ref('')
const orderDetail = ref({})

const tabs = [
    { id: 'order-summary', title: 'Order Summary' },
    { id: 'costing', title: 'Costing' },
    { id: 'production', title: 'Production' },
    { id: 'delivery-challan', title: 'Delivery Challan' },
    { id: 'invoice', title: 'Invoice' },
    { id: 'money-receipt', title: 'Money Receipt' }
]

const tabComponents = {
    'order-summary': OrderSummaryTab,
    'costing': CostingTab,
    'production': ProductionTab,
    'delivery-challan': DeliveryChallanTab,
    'invoice': InvoiceTab,
    'money-receipt': MoneyReceiptTab
}

const activeTab = ref(tabs[0])

const tabCounts = computed(() => {
    const counts = {
        'order-summary': null,
        'costing': null,
        'production': null,
        'delivery-challan': null,
        'invoice': null,
        'money-receipt': null
    }
    if (!orderDetail.value.costing) return counts
    counts.costing = orderDetail.value.costing.length
    counts.production = orderDetail.value.production.length
    counts['delivery-challan'] = orderDetail.value.delivery_challan.length
    counts.invoice = orderDetail.value.sale_invoices.length
    counts['money-receipt'] = orderDetail.value.money_receipts.length
    return counts
})

function getQuery() {
    return `?company_id=${route.params.companyId}`
}

async function getDetail() {
    loading.value = true
    errorState.value = ''

    await fetchOrderTrackerDetail(getQuery(), route.params.saleMasterId).then((res) => {
        if (!res.status) {
            errorState.value = 'Unable to load order details. Please try again.'
            return
        }
        orderDetail.value = res.data
    }).catch(() => {
        errorState.value = 'Unable to load order details. Please try again.'
    }).finally(() => {
        loading.value = false
    })
}

async function onClickToRedirect(item, type) {
    const routes = {
        'costing': 'product-costing-add',
        'production': 'production-add',
        'challan': 'create-challan'
    }
    const tabs = {
        'costing': 'costing',
        'production': 'production',
        'challan': 'delivery-challan'
    }

    let params = cloneDeep(route.params)
    let query = {
        sales_general_id: item.id,
        type: 'order-tracker-detail',
        sale_master_id: route.params.saleMasterId,
        tab: tabs[type],
        start: route.query.start,
        end: route.query.end
    }

    if (type === 'challan') {
        params.salesId = route.params.saleMasterId
    }

    await router.push({
        name: routes[type],
        params: params,
        query: query
    })
}

async function onClickToEditProduction(production) {
    await router.push({
        name: 'production-edit',
        params: {
            companyId: route.params.companyId,
            moduleId: route.params.moduleId,
            menuId: route.params.menuId,
            pageId: route.params.pageId,
            productionId: production.id
        },
        query: {
            type: 'order-tracker-detail',
            sale_master_id: route.params.saleMasterId,
            start: route.query.start,
            end: route.query.end
        }
    })
}

async function onClickToEditChallan(challan) {
    await router.push({
        name: 'delivery-challan-edit',
        params: {
            companyId: route.params.companyId,
            moduleId: route.params.moduleId,
            menuId: route.params.menuId,
            pageId: route.params.pageId,
            salesOrderId: challan.sales_order_master_id
        },
        query: {
            challan_id: challan.id,
            type: 'order-tracker-detail',
            sale_master_id: route.params.saleMasterId,
            tab: 'delivery-challan',
            start: route.query.start,
            end: route.query.end
        }
    })
}

async function onClickToEditCosting(costing) {
    await router.push({
        name: 'product-costing-edit',
        params: Object.assign({}, route.params, { productCostingId: costing.id }),
        query: Object.assign({}, route.query, {
            sale_master_id: route.params.saleMasterId,
            redirect_route: 'order-production-tracker-detail',
            tab: 'costing'
        })
    })
}

function setActiveTab(tab) {
    activeTab.value = tab
    router.replace({
        path: route.path,
        query: Object.assign({}, route.query, { tab: tab.id })
    })
}

function restoreActiveTab() {
    const currentTab = route.query.tab
    if (!currentTab) return
    const matched = tabs.find(tab => tab.id === currentTab)
    if (!matched) return
    activeTab.value = matched
}

function navigateToListPage() {
    router.push({
        name: 'order-production-tracker',
        params: route.params,
        query: route.query
    })
}

onMounted(() => {
    restoreActiveTab()
    getDetail()
})
</script>

<style scoped>
.nav-tabs .nav-link {
    cursor: pointer;
}
</style>
